import { useEffect, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Key, Plus, Copy, Trash2, Power, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AdminApiKeys() {
  const [keys, setKeys] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", kitchen_id: "" });
  const [newlyCreated, setNewlyCreated] = useState(null);

  useEffect(() => {
    fetchKeys();
    fetchKitchens();
  }, []);

  const fetchKeys = async () => {
    try {
      const res = await fetch(`${API}/admin/api-keys`, { credentials: "include" });
      if (res.ok) setKeys(await res.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const fetchKitchens = async () => {
    try {
      const res = await fetch(`${API}/kitchens`, { credentials: "include" });
      if (res.ok) setKitchens(await res.json());
    } catch { /* silent */ }
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.kitchen_id) {
      toast.error("Name and kitchen are required");
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/admin/api-keys`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const data = await res.json();
        setNewlyCreated(data);
        setShowCreate(false);
        setForm({ name: "", kitchen_id: "" });
        fetchKeys();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to create API key");
      }
    } catch { toast.error("Network error"); }
    finally { setCreating(false); }
  };

  const handleToggle = async (key_id) => {
    try {
      const res = await fetch(`${API}/admin/api-keys/${key_id}/toggle`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) { toast.success("Status updated"); fetchKeys(); }
      else toast.error("Failed to toggle");
    } catch { toast.error("Network error"); }
  };

  const handleDelete = async (key_id) => {
    if (!window.confirm("Delete this API key permanently? Any integrations using it will stop working.")) return;
    try {
      const res = await fetch(`${API}/admin/api-keys/${key_id}`, {
        method: "DELETE", credentials: "include",
      });
      if (res.ok) { toast.success("API key deleted"); fetchKeys(); }
      else toast.error("Failed to delete");
    } catch { toast.error("Network error"); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed")
    );
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-api-keys">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Key className="w-6 h-6 text-green-600" />
            <h1 className="text-xl font-bold text-gray-900">API Keys</h1>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="create-api-key-btn"
          >
            <Plus size={16} className="mr-1" /> Create Key
          </Button>
        </div>

        {/* Usage instructions */}
        <div className="bg-green-50 border border-green-100 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-2">
            <ShieldCheck size={18} className="text-green-600 shrink-0 mt-0.5" />
            <div className="text-xs text-gray-700 leading-relaxed">
              <p className="font-semibold text-green-800 mb-1">How to use</p>
              <p>Call <code className="bg-white px-1.5 py-0.5 rounded border text-green-700">GET /api/public/sales?start_date=YYYY-MM-DD&amp;end_date=YYYY-MM-DD</code></p>
              <p className="mt-1">Include the header <code className="bg-white px-1.5 py-0.5 rounded border text-green-700">X-API-Key: &lt;your-key&gt;</code>. Each key is scoped to a single kitchen.</p>
              <p className="mt-1 text-gray-500">Response excludes cancelled/refunded orders. Taxes are split by component (SGST, CGST, etc.).</p>
              <p className="mt-1 text-gray-500"><strong>Rate limit:</strong> 4 requests per minute per API key (HTTP 429 on exceed).</p>
            </div>
          </div>
        </div>

        {/* Keys table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-gray-600">
                  <th className="py-2.5 px-4 font-semibold">Name</th>
                  <th className="py-2.5 px-4 font-semibold">Kitchen</th>
                  <th className="py-2.5 px-4 font-semibold">Prefix</th>
                  <th className="py-2.5 px-4 font-semibold">Created</th>
                  <th className="py-2.5 px-4 font-semibold">Last Used</th>
                  <th className="py-2.5 px-4 font-semibold">Status</th>
                  <th className="py-2.5 px-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" className="p-8 text-center"><Loader2 className="inline w-5 h-5 animate-spin text-gray-400" /></td></tr>
                ) : keys.length === 0 ? (
                  <tr><td colSpan="7" className="p-8 text-center text-gray-500">No API keys yet. Click <b>Create Key</b> to generate one.</td></tr>
                ) : keys.map((k) => (
                  <tr key={k.key_id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`apikey-row-${k.key_id}`}>
                    <td className="py-2.5 px-4 font-medium text-gray-900">{k.name}</td>
                    <td className="py-2.5 px-4 text-gray-700">{k.kitchen_code || "—"}</td>
                    <td className="py-2.5 px-4 font-mono text-xs text-gray-600">{k.key_prefix}…</td>
                    <td className="py-2.5 px-4 text-gray-500 text-xs">{formatDate(k.created_at)}</td>
                    <td className="py-2.5 px-4 text-gray-500 text-xs">{formatDate(k.last_used_at)}</td>
                    <td className="py-2.5 px-4">
                      {k.is_active ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">Active</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-600">Disabled</span>
                      )}
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => handleToggle(k.key_id)}
                          className="p-1.5 text-gray-500 hover:text-green-600 rounded-lg hover:bg-gray-100"
                          title={k.is_active ? "Disable" : "Enable"}
                          data-testid={`toggle-apikey-${k.key_id}`}
                        >
                          <Power size={15} />
                        </button>
                        <button
                          onClick={() => handleDelete(k.key_id)}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200"
                          title="Delete permanently"
                          data-testid={`delete-apikey-${k.key_id}`}
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create API Key</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="apikey-name" className="text-xs font-semibold text-gray-700">Name</Label>
              <Input
                id="apikey-name"
                data-testid="apikey-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Tally Import"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">Kitchen</Label>
              <Select value={form.kitchen_id} onValueChange={(v) => setForm((f) => ({ ...f, kitchen_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="apikey-kitchen-select"><SelectValue placeholder="Choose kitchen" /></SelectTrigger>
                <SelectContent>
                  {kitchens.map((k) => (
                    <SelectItem key={k.kitchen_id} value={k.kitchen_id}>
                      {k.kitchen_code ? `${k.kitchen_code} — ` : ""}{k.name || k.locality || k.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="submit-create-apikey"
              >
                {creating ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Show-once-after-creation dialog */}
      <Dialog open={!!newlyCreated} onOpenChange={(open) => { if (!open) setNewlyCreated(null); }}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>API Key Created</DialogTitle></DialogHeader>
          {newlyCreated && (
            <div className="space-y-3 min-w-0">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                This is the only time the full key will be shown. Copy and store it securely.
              </div>
              <div className="min-w-0">
                <Label className="text-xs font-semibold text-gray-700">API Key</Label>
                <div className="mt-1 flex gap-2 min-w-0">
                  <Input readOnly value={newlyCreated.api_key} className="font-mono text-xs flex-1 min-w-0" data-testid="new-apikey-value" />
                  <Button
                    type="button"
                    onClick={() => copyToClipboard(newlyCreated.api_key)}
                    className="bg-green-600 hover:bg-green-700 text-white shrink-0"
                    data-testid="copy-apikey-btn"
                  >
                    <Copy size={14} className="mr-1" /> Copy
                  </Button>
                </div>
              </div>
              <div className="text-xs text-gray-600 min-w-0">
                <p><span className="font-semibold">Kitchen:</span> {newlyCreated.kitchen_code}</p>
                <p className="mt-2"><span className="font-semibold">Usage:</span></p>
                <pre className="mt-1 bg-gray-50 border rounded p-2 text-[11px] whitespace-pre-wrap break-all max-w-full">
{`curl -H "X-API-Key: ${newlyCreated.api_key}" \\
  "${window.location.origin}/api/public/sales?start_date=2026-04-01&end_date=2026-04-30"`}
                </pre>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setNewlyCreated(null)} className="bg-gray-700 hover:bg-gray-800 text-white">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
